// ─── AVATAR PARSER CONFIGURATION ─────────────────────────────────────────────
// Central constants for the avatar filename parser.
// All category maps, color sets, layer orders, and supported extensions live here.

export const PARSER_VERSION = '1.0.0';

// ─── SUPPORTED EXTENSIONS ────────────────────────────────────────────────────

export const SUPPORTED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp']);

export const MIME_TYPE_MAP = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

// ─── CATEGORY PREFIX MAP ─────────────────────────────────────────────────────
// Maps filename prefix tokens to canonical category slugs.
// Longer/more-specific prefixes must be checked before shorter ones.

export const CATEGORY_PREFIX_MAP = {
  // bodies
  body: 'bodies',
  // eyes
  eyes: 'eyes',
  // eyebrows
  eyebrows: 'eyebrows',
  // mouths
  mouth: 'mouths',
  // hair styles
  hair: 'hair_styles',
  // facial hair — multi-token prefix, handled specially
  facial_hair: 'facial_hair',
  // clothing
  clothing: 'clothing',
  // accessories
  accessory: 'accessories',
  // fantasy — special prefixes
  wings: 'fantasy',
  donkey_head: 'fantasy',
  donkey_costume: 'fantasy',
  lion_costume: 'fantasy',
  jester_hat: 'fantasy',
  donkey: 'fantasy',
  lion: 'fantasy',
  jester: 'fantasy',
};

// ─── FOLDER TO CATEGORY MAP ──────────────────────────────────────────────────

export const FOLDER_TO_CATEGORY = {
  bodies: 'bodies',
  eyes: 'eyes',
  eyebrows: 'eyebrows',
  mouths: 'mouths',
  hair_styles: 'hair_styles',
  facial_hair: 'facial_hair',
  clothing: 'clothing',
  accessories: 'accessories',
  fantasy: 'fantasy',
};

// ─── KNOWN COLORS ─────────────────────────────────────────────────────────────

export const KNOWN_COLORS = new Set([
  'black', 'brown', 'blue', 'gray', 'grey', 'hazel', 'green', 'red', 'gold',
  'purple', 'white', 'cream', 'tan', 'blonde', 'auburn', 'navy', 'burgundy',
  'crimson', 'emerald', 'plum', 'pink', 'rainbow', 'orange', 'yellow', 'silver',
  'pearl', 'rose', 'dark', 'light', 'porcelain', 'beige', 'deep', 'teal',
  'cyan', 'magenta', 'violet', 'indigo', 'maroon', 'olive',
]);

// ─── KNOWN BODY TYPES ────────────────────────────────────────────────────────

export const KNOWN_BODY_TYPES = new Set(['male', 'female', 'neutral', 'nonbinary']);

// ─── KNOWN GENDER TOKENS ─────────────────────────────────────────────────────

export const KNOWN_GENDERS = new Set(['male', 'female', 'neutral', 'nonbinary']);

// ─── KNOWN EYE STYLES ────────────────────────────────────────────────────────

export const KNOWN_EYE_STYLES = new Set([
  'classic', 'happy', 'wide', 'sleepy', 'serious', 'smiling', 'angry',
]);

// ─── KNOWN EYEBROW STYLES ────────────────────────────────────────────────────

export const KNOWN_EYEBROW_STYLES = new Set([
  'straight', 'curved', 'dramatic', 'thin', 'thick', 'worried', 'villain',
]);

// ─── KNOWN MOUTH STYLES ──────────────────────────────────────────────────────

export const KNOWN_MOUTH_STYLES = new Set([
  'smile', 'grin', 'neutral', 'open', 'smirk', 'surprised', 'sad', 'determined',
]);

// ─── KNOWN HAIR STYLES ───────────────────────────────────────────────────────

export const KNOWN_HAIR_STYLES = new Set([
  'short', 'long', 'curly', 'wavy', 'braids', 'bun', 'ponytail', 'curls',
  'bald', 'shakespeare',
]);

// ─── KNOWN CLOTHING SUBCATEGORIES ────────────────────────────────────────────
// Multi-token subcategory names for clothing items.

export const KNOWN_CLOTHING_SUBCATEGORIES = new Set([
  'doublet', 'royal_tunic', 'peasant_shirt', 'noble_jacket', 'corset_top',
  'dress_bodice', 'tights', 'skirt', 'dress', 'noble_pants', 'ruff_small',
  'ruff_large', 'lace_collar',
]);

// ─── KNOWN FANTASY SPECIAL TYPES ─────────────────────────────────────────────

export const KNOWN_FANTASY_SPECIAL_TYPES = new Set([
  'wings', 'donkey_head', 'donkey_costume', 'lion_costume', 'jester_hat',
]);

// ─── KNOWN FANTASY WING STYLES ───────────────────────────────────────────────

export const KNOWN_WING_STYLES = new Set([
  'small', 'large', 'butterfly', 'glow', 'leaf', 'crystal',
]);

// ─── KNOWN DONKEY HEAD VARIANTS ──────────────────────────────────────────────

export const KNOWN_DONKEY_HEAD_VARIANTS = new Set([
  'smiling', 'surprised', 'sleepy', 'crown', 'flower', 'jester',
]);

// ─── KNOWN COSTUME VARIANTS ──────────────────────────────────────────────────

export const KNOWN_COSTUME_VARIANTS = new Set([
  'fluffy', 'royal', 'floppy', 'comic',
]);

// ─── LAYER ORDER MAP ─────────────────────────────────────────────────────────
// Default layer_order values for rendering stacked avatar layers.

export const LAYER_ORDER_MAP = {
  bodies: 10,
  eyes: 40,
  eyebrows: 45,
  mouths: 50,
  hair_styles: 60,
  facial_hair: 65,
  clothing: 80,
  accessories: 90,
  // fantasy sub-types
  fantasy_wings: 5,
  fantasy_donkey_head: 70,
  fantasy_costume: 75,
  fantasy_jester_hat: 85,
  // fantasy default
  fantasy: 70,
};

// ─── CATEGORY LAYER DEFAULTS ─────────────────────────────────────────────────
// Convenience alias used by ingest service.

export const CATEGORY_LAYER_DEFAULTS = LAYER_ORDER_MAP;

// ─── PARSER STATUS ENUM ──────────────────────────────────────────────────────

export const PARSER_STATUS = {
  VALID: 'valid',
  WARNING: 'warning',
  REVIEW_REQUIRED: 'review_required',
  INVALID: 'invalid',
};
